/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ClusterId } from './cluster';
import { UuidV4 } from './common';
import { Brand } from './util';

export type Cluster3xNodeId = UuidV4 | string;

export type Cluster3xNodeName = string;
export type Cluster3xPhysicalTopology = { name: Cluster3xNodeName }[];

export type InitializeCluster3xRequestBody = {
  clusterId: ClusterId;
  // BE fills the value if empty
  clusterName?: string;
  cmgNodes: Cluster3xNodeName[];
  metaStorageNodes: Cluster3xNodeName[];
  configList: string[];
};

export type Cluster3xTopology = {
  nodes: Cluster3xNode[];
};
export type Cluster3xNode = {
  /**
   * Unique ID. Changes on restart.
   */
  id: Cluster3xNodeId;
  /**
   * Unique ID. Persistent across restarts.
   */
  name: Cluster3xNodeName;
  host: string;
  port: number;
  httpPort: number | -1;
  httpsPort: number | -1;
  status: 'RUNNING' | 'VALIDATING';
  cmg: boolean;
  ms: boolean;
  type: 'PHYSICAL' | 'LOGICAL';
};

export const HTTP_API_3X_UNAUTHORIZED_CODE = 401;
export const HTTP_API_3X_AUTH_HEADER_NAME = 'X-SECURITY-TOKEN';

export type Cluster3xConnectionCredentials = { username: string; password: string };

export type GetCluster3xConnectionCredentialsResponse = {
  username?: string;
  connectionUrl: string;
  clientTlsEnabled: boolean | null;
  clientAddresses: string[] | null;
  connectorName?: ClusterConnectorName | null;
};

export type Cluster3xRestApiUrl = string;
export type Cluster3xClientConnectionUrl = string;
export interface Cluster3xConnectionTokenPayload {
  rest: {
    url: Cluster3xRestApiUrl;
  };
  client?: {
    addresses: Cluster3xClientConnectionUrl[];
    tls?: {
      enabled: boolean;
    };
  };
  connectorId?: ClusterConnectorId | null;
}
export type Cluster3xConnectionToken = Brand<string, 'Cluster3xConnectionToken'>;

// Cluster configuration

type ExporterName = string;

type Cluster3xExporterConfiguration = {
  // Represents exporter type
  exporterName: ExporterName;
  // Represents exporter name
  name: string;
  [key: string]: any;
};

export type ControlCenterExporterConfiguration = Cluster3xExporterConfiguration & {
  // full path to api including protocol
  endpoint: string;
  protocol: string;
  period: number;
  compression: string;
};

export type Cluster3xConfiguration = {
  ignite?: {
    metrics?: {
      exporters?: Cluster3xExporterConfiguration[];
      [key: string]: any;
    };
    [key: string]: any;
  };
  [key: string]: any;
};

export type Cluster3xMonitoringConfigurationRequestBody = {
  metricsMonitoring?: {
    enabled: boolean;
  };
  runningQueriesMonitoring?: {
    enabled: boolean;
  };
};

// Users

export type Cluster3xUserLogin = string;
export type Cluster3xUserPassword = string;

export interface Cluster3xUser {
  login: Cluster3xUserLogin;
}

export interface Cluster3xUsers {
  users: Cluster3xUser[];
}

export interface Cluster3xNewUser {
  login: Cluster3xUserLogin;
  password: Cluster3xUserPassword;
}

export type ClusterConnectorId = UuidV4;
export type ClusterConnectorName = string;
export interface ClusterConnector {
  id: ClusterConnectorId;
  name: ClusterConnectorName;
}

export type ClusterConnectors = ClusterConnector[];
